import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Globe, Plus, Trash2, CheckCircle, Clock, AlertCircle, Copy, ExternalLink, Loader2, RefreshCw, Star } from 'lucide-react';
import { toast } from 'sonner';

export default function DomainManager() {
  const [data, setData] = useState({ domains: [], subdomain: '', custom_domain: null });
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ domain: '', is_primary: false });
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(null);

  const fetch = async () => {
    try {
      const r = await api.get('/tenant/domains');
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const handleAdd = async () => {
    if (!form.domain.trim()) return toast.error('Masukkan nama domain');
    setSaving(true);
    try {
      await api.post('/tenant/domains', form);
      setShowDialog(false);
      setForm({ domain: '', is_primary: false });
      fetch();
      toast.success('Domain ditambahkan');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menambahkan domain');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async (domainId) => {
    setVerifying(domainId);
    try {
      await api.put(`/tenant/domains/${domainId}/verify`);
      fetch();
      toast.success('Domain berhasil diverifikasi');
    } catch (e) {
      toast.error('Gagal verifikasi domain');
    } finally {
      setVerifying(null);
    }
  };

  const handleSetPrimary = async (domainId) => {
    try {
      await api.put(`/tenant/domains/${domainId}/set-primary`);
      fetch();
      toast.success('Domain utama diperbarui');
    } catch (e) {
      toast.error('Gagal mengubah domain utama');
    }
  };

  const handleDelete = async (domainId) => {
    if (!window.confirm('Hapus domain ini?')) return;
    try {
      await api.delete(`/tenant/domains/${domainId}`);
      fetch();
      toast.success('Domain dihapus');
    } catch (e) {
      toast.error('Gagal menghapus domain');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Disalin ke clipboard');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle size={12} className="mr-1" /> Verified</Badge>;
      case 'pending_verification':
        return <Badge className="bg-amber-100 text-amber-700"><Clock size={12} className="mr-1" /> Pending</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700"><AlertCircle size={12} className="mr-1" /> Unknown</Badge>;
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  return (
    <div data-testid="domain-manager-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Domain Manager</h1>
          <p className="text-sm text-slate-500">Kelola subdomain dan custom domain toko Anda</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-domain-btn">
          <Plus size={16} className="mr-2" /> Tambah Domain
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Default Subdomain */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe size={18} className="text-[#2D6CDF]" /> Subdomain Default
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#2D6CDF]/10 flex items-center justify-center">
                  <Globe size={18} className="text-[#2D6CDF]" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{data.subdomain}.tokomoe.com</p>
                  <p className="text-xs text-slate-500">Subdomain gratis dari platform</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(`https://${data.subdomain}.tokomoe.com`)} data-testid="copy-subdomain-btn">
                  <Copy size={14} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => window.open(`https://${data.subdomain}.tokomoe.com`, '_blank')}>
                  <ExternalLink size={14} />
                </Button>
              </div>
            </div>
            <Badge className="mt-3 bg-emerald-100 text-emerald-700"><CheckCircle size={12} className="mr-1" /> Aktif</Badge>
          </CardContent>
        </Card>

        {/* DNS Instructions */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Cara Setup Custom Domain</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#2D6CDF] text-white flex items-center justify-center text-xs font-medium">1</span>
              <p className="text-slate-600">Tambahkan domain baru dengan tombol di atas</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#2D6CDF] text-white flex items-center justify-center text-xs font-medium">2</span>
              <p className="text-slate-600">Login ke panel DNS domain Anda (Cloudflare, Niagahoster, dll)</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#2D6CDF] text-white flex items-center justify-center text-xs font-medium">3</span>
              <div className="flex-1">
                <p className="text-slate-600">Tambahkan DNS record berikut:</p>
                <div className="mt-2 p-3 bg-slate-100 rounded font-mono text-xs space-y-2">
                  <div>
                    <p className="text-slate-400 mb-1">// Untuk domain utama (root, tanpa www)</p>
                    <p>Type: <strong>A</strong></p>
                    <p>Name: <strong>@</strong></p>
                    <p>Value: <strong>187.127.111.48</strong></p>
                  </div>
                  <div className="border-t border-slate-200 pt-2">
                    <p className="text-slate-400 mb-1">// Untuk www</p>
                    <p>Type: <strong>CNAME</strong></p>
                    <p>Name: <strong>www</strong></p>
                    <p>Target: <strong>platform.tokomoe.com</strong></p>
                  </div>
                </div>
                <p className="text-[11px] text-amber-600 mt-2">
                  ⚠️ Domain utama (root/@) <strong>harus pakai A record</strong> ke IP di atas — CNAME tidak bisa untuk root di kebanyakan provider. Hapus dulu A record lama (mis. parkir Hostinger) kalau ada.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#2D6CDF] text-white flex items-center justify-center text-xs font-medium">4</span>
              <p className="text-slate-600">Klik tombol Verifikasi setelah DNS propagasi (5-30 menit). SSL akan otomatis dipasang oleh sistem.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Custom Domains List */}
      {data.domains.length > 0 && (
        <Card className="border-slate-200 shadow-sm mt-6">
          <CardHeader>
            <CardTitle className="text-base">Custom Domains</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Domain</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Primary</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {data.domains.map(d => (
                  <tr key={d.id} className="border-b border-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Globe size={16} className="text-slate-400" />
                        <span className="font-medium text-slate-900">{d.domain}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(d.status)}</td>
                    <td className="py-3 px-4">
                      {d.is_primary ? (
                        <Badge className="bg-[#2D6CDF]/10 text-[#2D6CDF]"><Star size={12} className="mr-1" /> Primary</Badge>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => handleSetPrimary(d.id)} className="text-xs" data-testid={`set-primary-${d.id}`}>
                          Set Primary
                        </Button>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {d.status === 'pending_verification' && (
                          <Button variant="outline" size="sm" onClick={() => handleVerify(d.id)} disabled={verifying === d.id} data-testid={`verify-domain-${d.id}`}>
                            {verifying === d.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />}
                            Verifikasi
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(d.id)} data-testid={`delete-domain-${d.id}`}>
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Add Domain Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tambah Custom Domain</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Domain</Label>
              <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="contoh: toko-saya.com" data-testid="domain-input" />
              <p className="text-xs text-slate-500 mt-1">Masukkan domain tanpa http:// atau https://</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_primary} onCheckedChange={(v) => setForm({ ...form, is_primary: v })} />
              <Label>Jadikan domain utama</Label>
            </div>
            <Button onClick={handleAdd} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-domain-btn">
              {saving && <Loader2 size={14} className="animate-spin mr-2" />}
              Tambah Domain
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
